import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import config from 'config';
import { formatCurrency } from 'utils';
import GlobalContext from 'site/context';
import Container from 'common/container';
import Button from 'common/button';
import useBreakpoint from 'hooks/use-breakpoint';
import ContributeContext from '../context';
import StepTitle from '../step-title';
import PresetTile from '../amount-tile-preset';
import CustomTile from '../amount-tile-custom';
import AmountTable from '../amount-table';
import PaymentButtons from '../payment-buttons';
import BackpledgePrompt from './backpledge-prompt';

const SelectAmount = ({ showPaymentButtons, onAmountChange, onBackpledgeChange, onContinue }) => {
    const { campaign, type, amount } = useContext(ContributeContext);
    const { showFlash } = useContext(GlobalContext);
    const [isCustom, setIsCustom] = useState(false);
    const [showBackpledge, setShowBackpledge] = useState(false);
    const { md } = useBreakpoint();
    const mobile = !md;

    const isPledge = type === 'pledge';
    const metricSingular = campaign.metricName?.singular;
    const metricPlural = campaign.metricName?.plural;
    const pastPerformance = campaign.stats.performanceActualTotal;
    const remainingPerformance = campaign.stats.performanceRemaining;
    const presets = isPledge ? campaign.pledgePresets : campaign.donatePresets;

    let title =
        amount != null ? `You selected ${formatCurrency(amount)} (US)` : `Select ${type} amount`;
    if (isPledge) title += ` per ${metricSingular}`;

    const getEstimatedTotal = pledge => pledge * remainingPerformance;

    const handleContinue = () => {
        // Ensure contribution is above our minimum amounts
        const minimum = isPledge
            ? config('/minimum_pledge_amount')
            : config('/minimum_donation_amount');

        if (amount < minimum) {
            const formattedMinimum = formatCurrency(minimum);
            onAmountChange(minimum);

            showFlash({
                type: 'warning',
                title: 'Amount Adjusted',
                message: `Your ${type} amount was adjusted to our minimum amount of ${formattedMinimum}.`,
                duration: 6000,
            });
        }

        // Ask the user to backpledge if applicable
        if (isPledge && pastPerformance) return setShowBackpledge(true);

        onContinue();
    };

    return (
        <>
            {isPledge && (
                <BackpledgePrompt
                    show={showBackpledge}
                    amount={pastPerformance * amount}
                    onAccept={() => {
                        onBackpledgeChange(true);
                        onContinue();
                    }}
                    onDecline={() => {
                        onBackpledgeChange(false);
                        onContinue();
                    }}
                    onClose={() => setShowBackpledge(false)}
                />
            )}
            <Container className="pt-8 pb-16 flex-1 flex flex-col justify-start max-w-xl">
                <div className="mt-12 rounded-lg lg:bg-white lg:shadow lg:p-16">
                    <p className="font-medium mb-2 text-gray-30 text-center">{campaign.name}</p>
                    <StepTitle
                        title={title}
                        subtitle={
                            isPledge
                                ? `(Based on ${remainingPerformance} estimated ${metricPlural})`
                                : null
                        }
                    />

                    <div className="flex flex-wrap md:flex-no-wrap justify-center items-stretch -ml-4 -mt-4">
                        {presets.map(preset => (
                            <div
                                className={cx('w-1/2 pl-4 pt-4 md:w-1/5', {
                                    'md:flex-shrink': isCustom,
                                })}
                                key={preset}
                            >
                                <PresetTile
                                    amount={formatCurrency(preset)}
                                    estimate={
                                        isPledge ? formatCurrency(getEstimatedTotal(preset)) : null
                                    }
                                    active={!isCustom && amount === preset}
                                    onClick={() => {
                                        onAmountChange(preset);
                                        setIsCustom(false);
                                    }}
                                />
                            </div>
                        ))}
                        <div
                            className={cx('w-full pl-4 pt-4', {
                                'md:w-1/5': !isCustom,
                                'md:w-1/3': isCustom,
                            })}
                        >
                            <CustomTile
                                label={mobile ? `Custom ${type} amount` : 'Custom'}
                                active={isCustom}
                                onClick={() => setIsCustom(true)}
                                onChange={x => onAmountChange(Number(x) * 100)}
                                value={(amount ?? 0) / 100}
                                autoFocus
                            />
                        </div>
                    </div>

                    {isPledge && (
                        <div className="mt-8 md:mt-12">
                            <AmountTable
                                data={[
                                    {
                                        label: amount ? `${formatCurrency(amount)} Pledge` : '-',
                                        performance: `x ${remainingPerformance}`,
                                        amount: amount
                                            ? formatCurrency(getEstimatedTotal(amount))
                                            : '-',
                                        muted: !amount,
                                    },
                                ]}
                                performanceLabel={`Est. ${metricPlural}`}
                                showPerformanceColumn
                            />
                        </div>
                    )}

                    {showPaymentButtons ? (
                        <PaymentButtons disabled={!amount} onContinue={handleContinue} />
                    ) : (
                        <Button
                            color={amount ? 'green' : 'light'}
                            size="md"
                            className={cx(
                                'mt-8 md:mt-12 text-16 font-medium block w-full mx-auto max-w-sm',
                                { 'pointer-events-none': !amount }
                            )}
                            onClick={handleContinue}
                        >
                            Continue
                        </Button>
                    )}
                </div>
            </Container>
        </>
    );
};

SelectAmount.propTypes = {
    onAmountChange: PropTypes.func.isRequired,
    onBackpledgeChange: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    showPaymentButtons: PropTypes.bool,
};

SelectAmount.defaultProps = {
    showPaymentButtons: false,
};

export default SelectAmount;
